import {
    forwardRef,
    useImperativeHandle,
    useMemo,
    useLayoutEffect,
    KeyboardEventHandler,
    ReactElement,
    ReactNode,
    useState,
    useRef,
    useEffect,
    useCallback,
    cloneElement,
    LegacyRef,
    MouseEventHandler
} from 'react';
import styles from './ui-popover.module.scss';
import throttle from 'lodash/throttle';
import ReactDOM from 'react-dom';

export type UIPopoverPlacement =
    | 'auto'
    | 'bottom_right'
    | 'bottom_left'
    | 'left_top'
    | 'left_bottom'
    | 'right_top'
    | 'right_bottom'
    | 'top_left'
    | 'top_right';

const POPOVER_PLACEMENT_DEFAULT: UIPopoverPlacement = 'auto';

type UIPopoverContentProps = {
    children?: ReactNode;
    targetRef: React.MutableRefObject<HTMLButtonElement | null>;
    placement?: UIPopoverPlacement;
    width?: number;
    triggerNodeWidth?: number | null;
    triggerNodeHeight?: number | null;
    popoverPadding: number;
    isNestedPopover?: boolean;
    zIndex?: number;
    onClose: () => void;
};
const UIPopoverContent = forwardRef<HTMLDivElement, UIPopoverContentProps>(
    (props, ref) => {
        const {
            children,
            targetRef,
            placement = POPOVER_PLACEMENT_DEFAULT,
            width = 250,
            triggerNodeHeight,
            popoverPadding,
            isNestedPopover = false,
            zIndex,
            onClose
        } = props;

        type Position = {
            top: number;
            left: number;
            right: number;
            bottom: number;
        };
        const [position, setPosition] = useState<Position | null>(null);
        const contentRef = useRef<HTMLDivElement | null>(null);
        const [computedPlacement, setComputedPlacement] = useState(placement);

        // measure popover height so we can do auto placement
        const [popoverHeight, setPopoverHeight] = useState<number>(0);

        useEffect(() => {
            const updatePosition = () => {
                if (targetRef.current) {
                    const rect = targetRef.current.getBoundingClientRect();
                    setPosition({
                        top: rect.bottom + window.scrollY,
                        left: rect.left + window.scrollX,
                        right: rect.right + window.scrollX,
                        bottom: rect.bottom + window.scrollY
                    });
                }
            };

            updatePosition();
            window.addEventListener('resize', updatePosition);
            window.addEventListener('scroll', updatePosition);

            return () => {
                window.removeEventListener('resize', updatePosition);
                window.removeEventListener('scroll', updatePosition);
            };
        }, [targetRef]);

        // after render, measure content height
        useLayoutEffect(() => {
            if (contentRef.current) {
                setPopoverHeight(contentRef.current.offsetHeight);
            }
        }, [children, width]);

        // compute auto placement if necessary
        useEffect(() => {
            if (!position) return;
            if (placement !== 'auto') {
                setComputedPlacement(placement);
                return;
            }

            const edgeOfScreenPadding = 32;

            const availableSpaceBelow =
                window.innerHeight -
                (position.bottom - window.scrollY) -
                edgeOfScreenPadding;

            const isTopHalf = position.top < window.innerHeight / 2;
            const isLeftHalf = position.left < window.innerWidth / 2;

            if (isTopHalf && isLeftHalf) {
                // Top left quadrant
                setComputedPlacement('bottom_right');
            } else if (isTopHalf && !isLeftHalf) {
                // Top right quadrant
                setComputedPlacement('bottom_left');
            } else if (!isTopHalf && isLeftHalf) {
                // Bottom left quadrant
                if (availableSpaceBelow >= popoverHeight + popoverPadding) {
                    setComputedPlacement('bottom_right');
                } else {
                    setComputedPlacement('top_right');
                }
            } else if (!isTopHalf && !isLeftHalf) {
                // Bottom right quadrant
                if (availableSpaceBelow >= popoverHeight + popoverPadding) {
                    setComputedPlacement('bottom_left');
                } else {
                    setComputedPlacement('top_left');
                }
            }
        }, [placement, position, popoverHeight, popoverPadding, width]);

        const placementStyle = useMemo(() => {
            if (!position) {
                return {};
            }

            switch (computedPlacement) {
                case 'left_top':
                    return {
                        width,
                        top: position.bottom - popoverHeight,
                        left: position.left - width - popoverPadding
                    };
                case 'left_bottom':
                    return {
                        width,
                        top: position.top - (triggerNodeHeight || 0),
                        left: position.left - width - popoverPadding
                    };

                case 'right_top':
                    return {
                        width,
                        top: position.bottom - popoverHeight,
                        left: position.right + popoverPadding
                    };
                case 'right_bottom':
                    return {
                        width,
                        top: position.top - (triggerNodeHeight || 0),
                        left: position.right + popoverPadding
                    };

                case 'top_left':
                    return {
                        width,
                        top:
                            position.top -
                            popoverHeight -
                            popoverPadding -
                            (triggerNodeHeight || 0),
                        left: position.right - width
                    };
                case 'top_right':
                    return {
                        width,
                        top:
                            position.top -
                            popoverHeight -
                            popoverPadding -
                            (triggerNodeHeight || 0),
                        left: position.left
                    };

                case 'bottom_right':
                    return {
                        width,
                        top: popoverPadding + position.top,
                        left: position.left
                    };
                case 'bottom_left':
                    return {
                        width,
                        top: popoverPadding + position.top,
                        left: position.right - width
                    };
                default:
                    return {};
            }
        }, [
            width,
            computedPlacement,
            triggerNodeHeight,
            popoverPadding,
            position,
            popoverHeight
        ]);

        const onKeyDown: KeyboardEventHandler<HTMLDivElement> = (e) => {
            if (e.keyCode === 27) {
                e.stopPropagation();
                onClose();
            }
        };

        return ReactDOM.createPortal(
            <div
                ref={(node) => {
                    contentRef.current = node;
                    if (typeof ref === 'function') {
                        ref(node);
                    } else if (ref) {
                        ref.current = node;
                    }
                }}
                className={styles.popoverContent}
                style={
                    zIndex === undefined
                        ? placementStyle
                        : { ...placementStyle, zIndex }
                }
                tabIndex={-1}
                onKeyDown={onKeyDown}
                {...(isNestedPopover ? { 'data-nested-popover': true } : {})}
            >
                {children}
            </div>,
            document.body
        );
    }
);

type UIPopoverProps = {
    children?: ReactNode;
    triggerNode: ReactElement<{
        ref: LegacyRef<HTMLButtonElement>;
        onClick: MouseEventHandler<HTMLButtonElement>;
        onMouseOver: MouseEventHandler<HTMLButtonElement>;
    }>;
    trigger?: 'click' | 'hover';
    onClickOutside?: () => void;
    placement?: UIPopoverPlacement;
    width?: number;
    closeOnClick?: boolean;
    popoverPadding?: number;
    isNestedPopover?: boolean;
    /**
     * Overrides the portalled content's stacking order. The default matches
     * `UIModalZIndex`, so a popover rendered inside a modal needs
     * `UISelectDropDownZIndex` to reliably paint above it.
     */
    zIndex?: number;
};

export type UIPropoverRef = {
    open: () => void;
    close: () => void;
};

export const UIPopover = forwardRef<UIPropoverRef, UIPopoverProps>(
    (props, ref) => {
        const {
            children,
            triggerNode,
            trigger = 'click',
            onClickOutside,
            placement = POPOVER_PLACEMENT_DEFAULT,
            width,
            closeOnClick = false,
            popoverPadding = 10,
            isNestedPopover = false,
            zIndex
        } = props;

        const [visible, setVisible] = useState(false);

        const _onShow: MouseEventHandler<HTMLButtonElement> = (e) => {
            e.stopPropagation();
            setVisible(true);
        };

        const _onClose = useCallback(() => {
            setVisible(false);
        }, []);

        const _onToggle: MouseEventHandler<HTMLButtonElement> = (e) => {
            e.stopPropagation();
            setVisible((prev) => !prev);
        };

        const _onClickOutside = useCallback(() => {
            _onClose();
            onClickOutside?.();
        }, [_onClose, onClickOutside]);

        const buttonNodeRef = useRef<HTMLButtonElement>(null);
        const contentNodeRef = useRef<HTMLDivElement>(null);

        useEffect(() => {
            const handleClickOutside = (e: MouseEvent) => {
                if (buttonNodeRef.current && contentNodeRef.current) {
                    const target = e.target as Node; // `Node` works for `.contains`

                    const didPressButton =
                        buttonNodeRef.current.contains(target);
                    const didPressContent =
                        contentNodeRef.current.contains(target);

                    // If it's an HTMLElement, we can use `.closest`
                    const isInNestedPopover =
                        target instanceof HTMLElement &&
                        target.closest('[data-nested-popover]');

                    if (closeOnClick) {
                        _onClose();
                    } else if (
                        !didPressButton &&
                        !didPressContent &&
                        !isInNestedPopover
                    ) {
                        _onClickOutside();
                    }
                }
            };

            document.addEventListener('mousedown', handleClickOutside);
            return () => {
                document.removeEventListener('mousedown', handleClickOutside);
            };
        }, [closeOnClick, _onClose, _onClickOutside]);

        useEffect(() => {
            if (trigger === 'hover') {
                const handleMouseMove = (e: MouseEvent) => {
                    if (buttonNodeRef.current && contentNodeRef.current) {
                        const isMouseInsideButton =
                            buttonNodeRef.current.contains(e.target as Node);
                        const isMouseInsideContent =
                            contentNodeRef.current.contains(e.target as Node);

                        if (!isMouseInsideButton && !isMouseInsideContent) {
                            _onClose();
                        }
                    }
                };
                const debouncedMouseMove = throttle(handleMouseMove, 300, {
                    leading: false,
                    trailing: true
                });

                document.addEventListener('mousemove', debouncedMouseMove);
                return () => {
                    document.removeEventListener(
                        'mousemove',
                        debouncedMouseMove
                    );
                };
            }
        }, [trigger, _onClose]);

        const _triggerNode = cloneElement(triggerNode, {
            ref: buttonNodeRef,
            onClick: _onToggle,
            onMouseOver: trigger === 'hover' ? _onShow : undefined
        });

        const [triggerNodeWidth, setTriggerNodeWidth] = useState<number | null>(
            null
        );
        const [triggerNodeHeight, setTriggerNodeHeight] = useState<
            number | null
        >(null);
        useEffect(() => {
            setTriggerNodeWidth(buttonNodeRef.current?.clientWidth || null);
            setTriggerNodeHeight(buttonNodeRef.current?.clientHeight || null);
        }, []);

        useImperativeHandle(ref, () => ({
            open: () => {
                setVisible(true);
            },
            close: () => {
                setVisible(false);
            }
        }));

        return (
            <div
                className={styles.container}
                {...(isNestedPopover ? { 'data-nested-popover': true } : {})}
            >
                {_triggerNode}

                {visible && (
                    <UIPopoverContent
                        ref={contentNodeRef}
                        targetRef={buttonNodeRef}
                        onClose={_onClose}
                        placement={placement}
                        width={width}
                        triggerNodeWidth={triggerNodeWidth}
                        triggerNodeHeight={triggerNodeHeight}
                        popoverPadding={popoverPadding}
                        isNestedPopover={isNestedPopover}
                        zIndex={zIndex}
                    >
                        {children}
                    </UIPopoverContent>
                )}
            </div>
        );
    }
);
